import { FeatureLineup } from '@mdlbeasts/nofomo.ui.web';
import { Stack } from '@mui/material';
import { useRouter } from 'next/router';
import { useCallback, useMemo } from 'react';
import { useIntl } from 'react-intl';

import {
  getDefaultLineupView,
  getLineupType,
  getLineupViewPath,
} from 'utils/lineup.utils';

import { LINEUP_ENTRY_PARAM, LineupKind } from 'constants/lineup.constants';
import { useLineupRoute } from 'hooks/useLineupRoute';
import { useLocale } from 'hooks/useLocale';
import { useLineupSelection } from 'queries/lineup/useLineupSelection';

import LineupSelectionLoading from 'components/@lineup/LineupSelection/LineupSelectionLoading';
import {
  toFeaturedArtists,
  toSelectionPrograms,
} from 'components/@lineup/normalizers';

/** Faces on a programme row before the rest become a count. */
const BILLING = 2;

interface Props {
  slug: string;
}

/** The lineup on an event page, and the way into it. */
const LineupSelection = ({ slug }: Props) => {
  const intl = useIntl();
  const router = useRouter();
  const { locale } = useLocale();
  const { settings } = useLineupRoute();
  const { programs, entries, hasLineup, timezone, isLoading } =
    useLineupSelection();

  const type = isLoading
    ? undefined
    : getLineupType({ hasXp: !!programs?.length, hasStandard: hasLineup });

  const rows = useMemo(
    () =>
      toSelectionPrograms({
        programs,
        billing: BILLING,
        othersLabel: (count) =>
          intl.formatMessage({ id: 'lineup.selection.others' }, { count }),
      }),
    [intl, programs],
  );

  const artists = useMemo(
    () =>
      toFeaturedArtists(
        { entries, locale, timezone, settings },
        { fallbackToAll: true },
      ),
    [entries, locale, settings, timezone],
  );

  const openLineup = useCallback(
    (kind?: LineupKind, entryId?: string) => {
      const path = getLineupViewPath(
        slug,
        getDefaultLineupView(kind ? 'xp' : 'standard', kind),
        kind,
      );

      void router.push(
        entryId ? `${path}?${LINEUP_ENTRY_PARAM}=${entryId}` : path,
      );
    },
    [router, slug],
  );

  const programActions = useMemo(
    () => ({
      pressProgram: (id: string) =>
        openLineup(
          programs?.find((program) => program.id === id)?.kind as LineupKind,
        ),
    }),
    [openLineup, programs],
  );

  const artistActions = useMemo(
    () => ({
      viewAll: () => openLineup(),
      pressArtist: (id: string) => openLineup(undefined, id),
    }),
    [openLineup],
  );

  if (!isLoading && !type) return null;

  return (
    <Stack sx={{ mb: { xs: 5, md: 8 } }}>
      {isLoading && <LineupSelectionLoading />}

      {type === 'xp' && (
        <FeatureLineup.Selection.Programs
          programs={rows}
          actions={programActions}
        />
      )}

      {type === 'standard' && (
        <FeatureLineup.Selection.Artists
          artists={artists}
          actions={artistActions}
        />
      )}
    </Stack>
  );
};

export default LineupSelection;
